import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    signal,
    ViewChild,
} from '@angular/core';
import {
    InventoryBundleFragment,
    ItemFragment,
} from '@control-tower/platform-wms';
import { SharedUiModule } from '@pf/shared-ui';
import {
    BarcodeScanEvent,
    CpwScanInputComponent,
} from './cpw-scan-input.component';
import { ItemFacade } from '@pf/platform-item-catalog/domain';
import { ContainerFacade, SlotFacade } from '@pf/platform-wms/domain';
import { HandlingUnitAssignmentService } from '@cpw/domain';
import { CpwInputErrorService } from '../services/cpw-input-error.service';
import { CreateMultiEntityBarcodeSearchFacade } from '@pf/shared/util-platform';
import { PFItemCatalogEntities, PFWmsEntities } from '@pf/shared-common';

export type ItemVerifiedEvent = {
    slotBarcode: string;
    itemBarcode: string;
};

@Component({
    selector: 'cpw-picking-item-scans',
    imports: [SharedUiModule, CpwScanInputComponent],
    templateUrl: './cpw-picking-item-scans.component.html',
    styleUrl: './cpw-picking-item-scans.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwPickingItemScansComponent implements OnInit {
    @ViewChild('slotScanInput') slotScanInput!: CpwScanInputComponent;
    @ViewChild('itemScanInput') itemScanInput!: CpwScanInputComponent;

    readonly itemFacade = inject(ItemFacade);
    readonly slotFacade = inject(SlotFacade);
    readonly containerFacade = inject(ContainerFacade);
    private readonly handlingUnitService = inject(
        HandlingUnitAssignmentService
    );
    private readonly inputErrorService = inject(CpwInputErrorService);

    item = input.required<ItemFragment>();
    bundle = input<InventoryBundleFragment | null>();
    isLTL = input(false);
    active = input(false);
    completed = input(false);

    @Input() verifiedLocationScanValue?: string;
    @Input() verifiedItemScanValue?: string;
    @Output() itemVerified = new EventEmitter<ItemVerifiedEvent>();

    itemScanEnabled = signal(false);
    slotVerified = signal(false);
    itemVerifiedState = signal(false);
    private scannedSlotBarcode = signal('');
    private scannedItemBarcode = signal('');

    itemBarcode = computed(() => {
        const itemBarcode = this.item()?.sku || this.item()?.code;
        if (this.isLTL()) {
            const containerBarcode = this.bundle()?.container?.lpn;
            return [itemBarcode, containerBarcode].filter(Boolean) as string[];
        }
        return itemBarcode || '';
    });

    slotBarcode = computed(() => {
        const container = this.bundle()?.container;
        return container?.slot?.barcode || container?.lpn || '';
    });

    locationFacade = computed(() => {
        if (this.requiresContainerScan()) {
            return this.containerFacade;
        }
        return this.slotFacade;
    });

    requiresContainerScan = computed(() => {
        const container = this.bundle()?.container;
        return !!container && !container.slot && !!container.equipment;
    });

    verifyItemLabel = computed(() => {
        return this.isLTL() ? 'Scan item or pallet' : 'Scan item';
    });

    itemOrPalletBarcodeSearchFacade = computed(() => {
        return this.isLTL()
            ? CreateMultiEntityBarcodeSearchFacade({
                  [PFWmsEntities.Container]: {
                      name: PFWmsEntities.Container,
                      facade: this.containerFacade,
                  },
                  [PFItemCatalogEntities.Item]: {
                      name: PFItemCatalogEntities.Item,
                      facade: this.itemFacade,
                  },
              })
            : this.itemFacade;
    });

    constructor() {
        effect(() => {
            if (this.active() && !this.completed()) {
                this.focus();
            }
        });
    }

    ngOnInit() {
        if (this.verifiedLocationScanValue && this.verifiedItemScanValue) {
            this.scannedSlotBarcode.set(this.verifiedLocationScanValue);
            this.scannedItemBarcode.set(this.verifiedItemScanValue);
            this.slotVerified.set(true);
            this.itemScanEnabled.set(false);
            this.itemVerifiedState.set(true);
            this.itemVerified.emit({
                slotBarcode: this.verifiedLocationScanValue,
                itemBarcode: this.verifiedItemScanValue,
            });
        }
    }

    focus() {
        if (this.verifiedLocationScanValue && this.verifiedItemScanValue) {
            return;
        }
        this.slotVerified.set(false);
        this.itemVerifiedState.set(false);
        this.itemScanEnabled.set(false);
        this.scannedSlotBarcode.set('');
        this.scannedItemBarcode.set('');
        this.focusSlotScanInput();
    }

    onSlotScanned(scan: BarcodeScanEvent) {
        if (!this.requiresContainerScan()) {
            this.completeSlotScan(scan);
            return;
        }

        const equipmentId = this.bundle()?.container?.equipment?.id;
        if (!equipmentId) {
            this.inputErrorService.showError(
                'Container is not assigned to equipment.',
                scan.scanInput
            );
            scan.processed.next(false);
            return;
        }

        this.handlingUnitService
            .addHandlingUnitById(equipmentId)
            .subscribe(assigned => {
                if (!assigned) {
                    this.inputErrorService.showError(
                        'Equipment cannot be assigned to your active session.',
                        scan.scanInput
                    );
                    scan.processed.next(false);
                    return;
                }
                this.completeSlotScan(scan);
            });
    }

    private completeSlotScan(scan: BarcodeScanEvent) {
        this.slotVerified.set(true);
        this.itemScanEnabled.set(true);
        this.scannedSlotBarcode.set(scan.scanInput);
        scan.processed.next(true);
        this.focusItemScanInput();
    }

    onItemScanned(scan: BarcodeScanEvent) {
        this.itemVerifiedState.set(true);
        this.scannedItemBarcode.set(scan.scanInput);
        scan.processed.next(true);
        this.itemVerified.emit({
            slotBarcode: this.scannedSlotBarcode(),
            itemBarcode: scan.scanInput,
        });
    }

    private focusSlotScanInput() {
        setTimeout(() => {
            this.slotScanInput.focus(true);
        });
    }

    private focusItemScanInput() {
        setTimeout(() => {
            this.itemScanInput.focus(true);
        });
    }
}
